import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type AnalyticsEventParams from 'src/core/models/AnalyticsEventParams';
import type AnalyticsUser from 'src/core/models/AnalyticsUser';
import {
	AnalyticsService,
	CrashlyticsService,
	FacebookService,
	LinkingService,
	TranslationService,
	ValidationService,
} from 'src/data/services/Services';

export const useServicesStore = create(
	combine({}, () => {
		const logEvent = ({
			name,
			params,
			user,
		}: {
			name: string;
			params: AnalyticsEventParams;
			user: AnalyticsUser | null;
		}): void => {
			try {
				AnalyticsService.sendEvent({ name, params, user }).catch((error: ApiRequestError) => {
					CrashlyticsService.captureEvent({ message: `Sending the analytics event ${name} failed.`, extra: { error } });
				});
			} catch (error: unknown) {
				CrashlyticsService.captureEvent({ message: `Sending the analytics event ${name} failed.`, extra: { error } });
			}
		};

		const setAnalyticsCollectionEnabled = (enabled: boolean) => AnalyticsService.setCollectionEnabled(enabled);

		const initializeFacebook = () => FacebookService.initialize();

		const captureEvent = (options: { message: string; extra?: Record<string, unknown> }) =>
			CrashlyticsService.captureEvent(options);

		const setCrashlyticsUser = (options: { id?: number | string }) => CrashlyticsService.setUser(options);

		const clearCrashlyticsUser = () => CrashlyticsService.clearUser();

		const getI18n = () => TranslationService.i18n;

		const openUrlExternally = ({ url }: { url: string }) => LinkingService.openUrlExternally({ url });

		const isValidEmail = ({ email }: { email: string }) => ValidationService.isValidEmail(email);

		return {
			logEvent,
			setAnalyticsCollectionEnabled,
			initializeFacebook,
			captureEvent,
			setCrashlyticsUser,
			clearCrashlyticsUser,
			getI18n,
			openUrlExternally,
			isValidEmail,
		};
	}),
);
